import { useEffect, useRef, useState } from "react";
import { clientsApi, vendorApi } from "../../services/businessApi";
import { useToast } from "../../context/ToastContext";
import { vocabularyFor } from "../../services/productVocabulary";
import { money } from "../../utils/format";
import { Modal } from "../common/Modal";
import { Button } from "../common/Button";
import { Field } from "../common/Field";

/**
 * Send wax, propolis or pollen to a vendor from the product's own card
 * (10 Oct, owner click-through: "There is no Send to vendor button in the
 * Other Products Pool ... that would be a good addition").
 *
 * The same call the vendor page's Send Product dialog makes
 * (`vendorApi.assign`), so the two cannot record different things; this
 * only lets you start from the product instead of the vendor. Packaged
 * (by a size you defined) or bulk (by the pound), priced per unit or per
 * pound. No varietal: wax has none, and the server refuses the pair.
 *
 * Shortfalls are refused by the server with the numbers, and shown as it
 * says them. Unlike pouring, sending stock you do not have is not
 * documenting work already done, so there is no override.
 */
export function SendToVendorModal({ productType, sizes = [], open, onClose, onSent }) {
  const { notify } = useToast();
  const vocab = vocabularyFor(productType);
  const noun = productType.toLowerCase();
  const usable = sizes.filter((s) => s.is_active !== false);
  const empty = { client_id: "", mode: usable.length ? "packaged" : "bulk",
                  bottle_size_id: "", bottle_count: "", pounds: "", unit_price: "", notes: "" };
  const [form, setForm] = useState(empty);
  const [vendors, setVendors] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    clientsApi.list()
      .then((r) => setVendors(r.clients || []))
      .catch(() => setVendors([]));
  }, [open]);

  const bulk = form.mode === "bulk";

  /* The size's default price (#520), set in Manage sizes. Fills the price
     when a size is picked; replaces it only if it is empty or is the one
     this put there for the previous size. Typed prices are left alone. */
  const autoPrice = useRef("");
  useEffect(() => {
    if (bulk || !form.bottle_size_id) return;
    const size = usable.find((s) => String(s.bottle_size_id) === String(form.bottle_size_id));
    if (size?.default_price == null) return;
    const prev = autoPrice.current;  // read now: the updater below runs later
    setForm((f) => (f.unit_price && f.unit_price !== prev ? f
      : { ...f, unit_price: String(size.default_price) }));
    autoPrice.current = String(size.default_price);
  }, [bulk, form.bottle_size_id]); // eslint-disable-line react-hooks/exhaustive-deps

  async function submit() {
    const payload = {
      client_id: Number(form.client_id),
      unit_price: Number(form.unit_price || 0),
      notes: form.notes || undefined,
      product_type: productType,
      ...(bulk
        ? { pounds: Number(form.pounds) }
        : { bottle_size_id: Number(form.bottle_size_id), bottle_count: Number(form.bottle_count) }),
    };
    if (!payload.client_id) return notify("Pick a vendor", "warning");
    // `> 0`, not falsy: -5 is truthy.
    if (bulk ? !(payload.pounds > 0)
             : !(payload.bottle_size_id && payload.bottle_count > 0)) {
      return notify(bulk ? "Enter how many pounds" : `Pick a size and how many ${vocab.units}`, "warning");
    }
    setBusy(true);
    try {
      const r = await vendorApi.assign(payload);
      notify(`Sent out — ${money(r.value)} owed`, "success");
      setForm(empty);
      onClose();
      onSent?.();
    } catch (err) {
      notify(err.apiMessage || "Could not record the delivery", "danger");
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`Send ${noun} to a vendor`}
      footer={<Button disabled={busy} onClick={submit}>{busy ? "Saving…" : "Record Delivery"}</Button>}
    >
      <div style={{ display: "grid", gap: "var(--space-3)" }}>
        <Field label="Vendor">
          {(id, aria) => (
            <select id={id} {...aria} className="touch-target" value={form.client_id}
              onChange={(e) => setForm((f) => ({ ...f, client_id: e.target.value }))}>
              <option value="">{vendors === null ? "Loading…" : "Choose a vendor…"}</option>
              {(vendors || []).map((v) => (
                <option key={v.client_id} value={v.client_id}>{v.name}</option>
              ))}
            </select>
          )}
        </Field>
        <Field label="What are you sending?">
          {(id, aria) => (
            <select id={id} {...aria} className="touch-target" value={form.mode}
              onChange={(e) => setForm((f) => ({ ...f, mode: e.target.value, bottle_size_id: "" }))}>
              {usable.length ? <option value="packaged">{`Packaged ${noun}, by the size`}</option> : null}
              <option value="bulk">{`Bulk ${noun}, by the pound`}</option>
            </select>
          )}
        </Field>
        {bulk ? (
          <Field label="How many pounds?"
            hint={`Comes straight out of the ${noun} pool — bulk is never packaged.`}>
            {(id, aria) => (
              <input id={id} {...aria} type="number" step="0.1" min="0.1" className="touch-target"
                value={form.pounds}
                onChange={(e) => setForm((f) => ({ ...f, pounds: e.target.value }))} />
            )}
          </Field>
        ) : (
          <>
            <Field label="Size">
              {(id, aria) => (
                <select id={id} {...aria} className="touch-target" value={form.bottle_size_id}
                  onChange={(e) => setForm((f) => ({ ...f, bottle_size_id: e.target.value }))}>
                  <option value="">Select a size...</option>
                  {usable.map((s) => (
                    <option key={s.bottle_size_id} value={s.bottle_size_id}>
                      {`${s.label} (${s.on_hand} on the shelf)`}
                    </option>
                  ))}
                </select>
              )}
            </Field>
            <Field label="How many?">
              {(id, aria) => (
                <input id={id} {...aria} type="number" min="1" className="touch-target"
                  value={form.bottle_count}
                  onChange={(e) => setForm((f) => ({ ...f, bottle_count: e.target.value }))} />
              )}
            </Field>
          </>
        )}
        <Field label={bulk ? "Price per pound" : `Price per ${vocab.unit}`}>
          {(id, aria) => (
            <input id={id} {...aria} type="number" step="0.01" min="0" className="touch-target"
              value={form.unit_price}
              onChange={(e) => setForm((f) => ({ ...f, unit_price: e.target.value }))} />
          )}
        </Field>
        <Field label="Note (optional)">
          {(id, aria) => (
            <input id={id} {...aria} className="touch-target" value={form.notes}
              onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} />
          )}
        </Field>
      </div>
    </Modal>
  );
}
